import type { Book, ManualMatchPair, ReconSettings } from "../types/api";
import type { SidebarSettings } from "../types/ui";

// Survive a browser refresh without re-uploading.
//
// A reconciliation lives entirely in React state, so F5 / an accidental tab close /
// a laptop sleep used to destroy the result AND every manual match and note made
// since — forcing a full re-upload and a ~10s re-run. But the expensive half (the
// PrematchState) is still on the server in Redis (a day of idle time) under a stable prematch_id,
// and /api/finalize rebuilds a COMPLETE result from that id alone in ~0.4s. So we
// persist the small identifying scalars here and replay them through /finalize.
//
// What is deliberately NOT stored:
//   - the result frames (up to ~75MB for Dubai — far past any storage quota, and
//     /finalize regenerates them authoritatively anyway),
//   - the uploaded File objects (not serialisable; see `filesLost` on the restored
//     session — the paths that need the raw bytes stay disabled until re-upload).
//
// sessionStorage, not localStorage: a reconciliation is per-tab working state, and
// tab-scoped storage means a shared machine can't leak one user's in-progress work
// into another's next visit. clear() is also called on logout/401 (same contract as
// resetColumnPins) so a sign-out leaves nothing behind.
//
// Every failure path degrades to "no saved session" and is swallowed — restore is a
// convenience and must never surface an error over a reconciliation.

const KEY = "cri_recon_session_v1";

export interface StoredSession {
  prematchId: string;
  // Which book the reconciliation was RUN in. Restored alongside it so a refresh can
  // never re-label one book's figures with the other's — the header would otherwise
  // show whatever book the tab happened to resolve to. Absent (a session stored before
  // the book axis existed) reads as AP, the same rule the server applies.
  book?: Book;
  // The AR store scope (blank on AP). Restored with the session so a refreshed
  // dashboard loads THIS customer's remembered matches, not another customer's.
  customerKey?: string;
  // The display segment on screen (Group Life / Non-Group Life). Absent = All business.
  segment?: string;
  insurerName: string;
  insurerSheetName: string;
  sessionPairs: ManualMatchPair[];
  notesCri: Record<string, string>;
  notesInsurer: Record<string, string>;
  bridgeRejections: string[];
  reconSettings: ReconSettings;
  sidebar: SidebarSettings;
}

export function save(s: StoredSession): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    // Quota exceeded / storage disabled (private mode). Restore is optional.
  }
}

export function load(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as StoredSession;
    // A prematch_id is the one field restore cannot work without.
    if (!p || typeof p.prematchId !== "string" || !p.prematchId) return null;
    return p;
  } catch {
    return null;
  }
}

export function clear(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* nothing to do */
  }
}
